import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { grnAPI, vendorsAPI, productsAPI, storesAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { 
  Package, Plus, Search, Save, FileText, Truck, CheckCircle,
  RefreshCw, Trash2, Eye, Calendar, IndianRupee, Building2,
  ClipboardList, ArrowLeft, Send, Calculator, History
} from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';
import HighlightText from '@/components/HighlightText';

const TAX_RATES = [
  { value: 0, label: '0%' },
  { value: 5, label: '5%' },
  { value: 12, label: '12%' },
  { value: 18, label: '18%' },
  { value: 28, label: '28%' }
];

const StoreGRNEntry = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const storeId = user?.entity_id;
  const storeName = user?.entity_name;
  
  // Tab state
  const [activeTab, setActiveTab] = useState('create');
  
  // GRN List state
  const [grnList, setGrnList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  
  // Vendors
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  
  // GRN Form state
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [eInvoiceNumber, setEInvoiceNumber] = useState('');
  const [supplierGSTIN, setSupplierGSTIN] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [grnItems, setGrnItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  // Product search
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const searchReqRef = useRef(0);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // View GRN
  const [selectedGRN, setSelectedGRN] = useState(null);

  // Fetch vendors
  const fetchVendors = useCallback(async () => {
    setVendorsLoading(true);
    try {
      const response = await vendorsAPI.list({ is_active: true });
      setVendors(response.data || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    } finally {
      setVendorsLoading(false);
    }
  }, []);

  // Fetch GRN list
  const fetchGRNList = useCallback(async () => {
    if (!storeId) return;
    
    setListLoading(true);
    try {
      const params = { store_id: storeId };
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await grnAPI.list(params);
      setGrnList(response.data || []);
    } catch (error) {
      console.error('Error fetching GRNs:', error);
    } finally {
      setListLoading(false);
    }
  }, [storeId, statusFilter]);

  useEffect(() => {
    fetchVendors();
    fetchGRNList();
  }, [fetchVendors, fetchGRNList]);

  // Search products
  const searchProducts = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    const reqId = ++searchReqRef.current;
    try {
      const response = await productsAPI.search(query.trim(), 20);
      if (reqId !== searchReqRef.current) return;
      setSearchResults(response.data || []);
    } catch (error) {
      console.error('Error searching products:', error);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      searchProducts(productSearch);
    }, 300);
    return () => clearTimeout(debounce);
  }, [productSearch, searchProducts]);

  // Select vendor
  const handleSelectVendor = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    setSelectedVendor(vendor);
    if (vendor?.gstin) {
      setSupplierGSTIN(vendor.gstin);
    }
  };

  // Add product to GRN
  const addProductToGRN = (product) => {
    if (grnItems.some(item => item.product_id === product.id)) {
      toast({ title: 'Already Added', description: 'Product is already in the GRN', variant: 'destructive' });
      return;
    }
    
    setGrnItems([...grnItems, {
      product_id: product.id,
      product_name: product.name,
      product_sku: product.sku,
      hsn_code: product.hsn_code || '',
      uom: product.uom_name || product.base_uom || 'PCS',
      received_qty: 1,
      accepted_qty: 1,
      rejected_qty: 0,
      unit_price: product.cost_price || 0,
      tax_rate: product.tax_rate || 18,
      batch_number: '',
      expiry_date: '',
      rejection_reason: ''
    }]);
    
    setProductSearch('');
    setSearchResults([]);
  };

  // Update item field
  const updateItemField = (index, field, value) => {
    const updated = [...grnItems];
    updated[index][field] = value;
    
    // Auto-calculate rejected qty when received or accepted changes
    if (field === 'received_qty' || field === 'accepted_qty') {
      const received = field === 'received_qty' ? parseFloat(value) || 0 : updated[index].received_qty;
      const accepted = field === 'accepted_qty' ? parseFloat(value) || 0 : updated[index].accepted_qty;
      updated[index].rejected_qty = Math.max(0, received - accepted);
      if (field === 'received_qty') {
        updated[index].accepted_qty = parseFloat(value) || 0;
        updated[index].rejected_qty = 0;
      }
    }
    
    setGrnItems(updated);
  };

  // Remove item
  const removeItem = (index) => {
    setGrnItems(items => items.filter((_, i) => i !== index));
  };

  // Calculate line total
  const calculateLineTotal = (item) => {
    const subtotal = (item.accepted_qty || 0) * (item.unit_price || 0);
    const tax = subtotal * ((item.tax_rate || 0) / 100);
    return subtotal + tax;
  };

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    let totalTax = 0;
    
    grnItems.forEach(item => {
      const lineSubtotal = (item.accepted_qty || 0) * (item.unit_price || 0);
      const lineTax = lineSubtotal * ((item.tax_rate || 0) / 100);
      subtotal += lineSubtotal;
      totalTax += lineTax;
    });
    
    return {
      subtotal: subtotal.toFixed(2),
      totalTax: totalTax.toFixed(2),
      grandTotal: (subtotal + totalTax).toFixed(2)
    };
  };

  // Reset form
  const resetForm = () => {
    setSelectedVendor(null);
    setInvoiceNumber('');
    setInvoiceDate(new Date().toISOString().split('T')[0]);
    setEInvoiceNumber('');
    setSupplierGSTIN('');
    setVehicleNumber('');
    setNotes('');
    setGrnItems([]);
    setProductSearch('');
    setSearchResults([]);
  };

  // Save GRN
  const handleSaveGRN = async (complete = false) => {
    // Validation
    if (!selectedVendor) {
      toast({ title: 'Error', description: 'Please select a vendor', variant: 'destructive' });
      return;
    }
    if (!invoiceNumber) {
      toast({ title: 'Error', description: 'Please enter invoice number', variant: 'destructive' });
      return;
    }
    if (grnItems.length === 0) {
      toast({ title: 'Error', description: 'Please add at least one product', variant: 'destructive' });
      return;
    }
    
    const hasValidItems = grnItems.some(item => (item.accepted_qty || 0) > 0);
    if (complete && !hasValidItems) {
      toast({ title: 'Error', description: 'At least one item must have accepted quantity > 0', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const grnData = {
        vendor_id: selectedVendor.id,
        store_id: storeId,
        invoice_number: invoiceNumber,
        invoice_date: invoiceDate,
        e_invoice_number: eInvoiceNumber || null,
        supplier_gstin: supplierGSTIN || null,
        vehicle_number: vehicleNumber || null,
        notes: notes || null,
        entry_type: 'store_direct',
        items: grnItems.map(item => ({
          product_id: item.product_id,
          received_qty: parseFloat(item.received_qty) || 0,
          accepted_qty: parseFloat(item.accepted_qty) || 0,
          rejected_qty: parseFloat(item.rejected_qty) || 0,
          unit_price: parseFloat(item.unit_price) || 0,
          tax_rate: parseFloat(item.tax_rate) || 0,
          batch_number: item.batch_number || null,
          expiry_date: item.expiry_date || null,
          rejection_reason: item.rejection_reason || null
        }))
      };
      
      const response = await grnAPI.createStoreGRN(grnData);
      const createdGRN = response.data;
      
      if (complete && createdGRN?.id) {
        await grnAPI.completeStoreGRN(createdGRN.id);
        toast({ title: 'Success', description: 'GRN completed and stock updated' });
      } else {
        toast({ title: 'Success', description: 'GRN saved as draft' });
      }
      
      resetForm();
      fetchGRNList();
      setActiveTab('history');
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to save GRN', 
        variant: 'destructive' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  // View GRN details
  const handleViewGRN = async (grn) => {
    try {
      const response = await grnAPI.get(grn.id);
      setSelectedGRN(response.data);
    } catch (error) {
      setSelectedGRN(grn);
    }
  };

  const totals = calculateTotals();

  return (
    <div className="min-h-screen bg-gray-50" data-testid="store-grn-entry-page">
      <Toaster />
      
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="p-4 lg:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <ClipboardList className="w-6 h-6 text-teal-600" />
                Goods Receipt Note (GRN)
              </h1>
              <p className="text-gray-500">{storeName} - Local Purchase Entry</p>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={fetchGRNList} variant="outline" className="gap-2">
                <RefreshCw className="w-4 h-4" /> Refresh
              </Button>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="px-4 lg:px-6">
          <TabsList className="bg-gray-100">
            <TabsTrigger value="create" className="data-[state=active]:bg-white gap-2">
              <Plus className="w-4 h-4" /> Create GRN
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-white gap-2">
              <History className="w-4 h-4" /> GRN History
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="p-4 lg:p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Create GRN Tab */}
          <TabsContent value="create" className="mt-0 space-y-6">
            {/* Vendor & Invoice Details */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Truck className="w-5 h-5 text-purple-600" />
                  Vendor & Invoice Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Vendor Selection */}
                  <div className="space-y-2">
                    <Label className="text-gray-700">Vendor / Supplier *</Label>
                    <Select 
                      value={selectedVendor?.id || ''} 
                      onValueChange={handleSelectVendor}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Select vendor..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white max-h-60">
                        {vendors.map(vendor => (
                          <SelectItem key={vendor.id} value={vendor.id}>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{vendor.name}</span>
                              <span className="text-xs text-gray-500">({vendor.code})</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Invoice Number */}
                  <div className="space-y-2">
                    <Label className="text-gray-700">Invoice Number *</Label>
                    <Input
                      value={invoiceNumber}
                      onChange={(e) => setInvoiceNumber(e.target.value)}
                      placeholder="Enter invoice number"
                      className="bg-white"
                    />
                  </div>
                  
                  {/* Invoice Date */}
                  <div className="space-y-2">
                    <Label className="text-gray-700">Invoice Date *</Label>
                    <Input
                      type="date"
                      value={invoiceDate}
                      onChange={(e) => setInvoiceDate(e.target.value)}
                      className="bg-white"
                    />
                  </div>
                  
                  {/* Supplier GSTIN */}
                  <div className="space-y-2">
                    <Label className="text-gray-700">Supplier GSTIN</Label>
                    <Input
                      value={supplierGSTIN}
                      onChange={(e) => setSupplierGSTIN(e.target.value.toUpperCase())}
                      placeholder="22AAAAA0000A1Z5"
                      maxLength={15}
                      className="bg-white"
                    />
                  </div>
                  
                  {/* E-Invoice Number (Optional) */}
                  <div className="space-y-2">
                    <Label className="text-gray-700">E-Invoice / IRN (Optional)</Label>
                    <Input
                      value={eInvoiceNumber}
                      onChange={(e) => setEInvoiceNumber(e.target.value)}
                      placeholder="E-Invoice reference (if applicable)"
                      className="bg-white"
                    />
                  </div>
                  
                  {/* Vehicle Number */}
                  <div className="space-y-2">
                    <Label className="text-gray-700">Vehicle Number (Optional)</Label>
                    <Input
                      value={vehicleNumber}
                      onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                      placeholder="MH12AB1234"
                      className="bg-white"
                    />
                  </div>
                </div>
                
                {/* Selected Vendor Info */}
                {selectedVendor && (
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-start gap-3">
                      <Building2 className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div>
                          <p className="text-purple-600 text-xs">Vendor Name</p>
                          <p className="font-medium text-gray-800">{selectedVendor.name}</p>
                        </div>
                        <div>
                          <p className="text-purple-600 text-xs">Code</p>
                          <p className="font-medium text-gray-800">{selectedVendor.code}</p>
                        </div>
                        <div>
                          <p className="text-purple-600 text-xs">GSTIN</p>
                          <p className="font-medium text-gray-800">{selectedVendor.gstin || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-purple-600 text-xs">Contact</p>
                          <p className="font-medium text-gray-800">{selectedVendor.mobile || selectedVendor.contact_mobile || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Product Entry */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="w-5 h-5 text-teal-600" />
                  Product Items
                </CardTitle>
                <CardDescription>Search and add products to this GRN</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Product Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    placeholder="Search products by name, SKU, or barcode..."
                    className="pl-10 bg-white"
                    data-testid="grn-product-search-input"
                  />
                  
                  {/* Search Results Dropdown */}
                  {searchResults.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto z-20">
                      {searchResults.map(product => (
                        <div
                          key={product.id}
                          className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 flex items-center justify-between"
                          data-testid={`grn-search-result-${product.id}`}
                          onClick={() => addProductToGRN(product)}
                        >
                          <div>
                            <p className="font-medium text-gray-800"><HighlightText text={product.name} query={productSearch} /></p>
                            <p className="text-xs text-gray-500">
                              SKU: <HighlightText text={product.sku} query={productSearch} /> • HSN: {product.hsn_code || 'N/A'} • Cost: ₹{product.cost_price || 0}
                            </p>
                          </div>
                          <Button size="sm" variant="ghost" className="text-teal-600">
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {searchLoading && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg p-3 text-center text-gray-500">
                      Searching...
                    </div>
                  )}
                </div>

                {/* Items Table */}
                {grnItems.length === 0 ? (
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">No products added yet</p>
                    <p className="text-sm">Search and add products above</p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="min-w-[200px]">Product</TableHead>
                          <TableHead className="w-24 text-center">HSN</TableHead>
                          <TableHead className="w-28 text-right">Received</TableHead>
                          <TableHead className="w-28 text-right">Accepted</TableHead>
                          <TableHead className="w-28 text-right">Rate (₹)</TableHead>
                          <TableHead className="w-24 text-center">Tax %</TableHead>
                          <TableHead className="w-32">Batch No.</TableHead>
                          <TableHead className="w-32">Expiry</TableHead>
                          <TableHead className="w-28 text-right">Total (₹)</TableHead>
                          <TableHead className="w-16"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {grnItems.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <p className="font-medium text-gray-800">{item.product_name}</p>
                              <p className="text-xs text-gray-500">{item.product_sku}</p>
                            </TableCell>
                            <TableCell className="text-center text-sm text-gray-600">
                              {item.hsn_code || '-'}
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="0"
                                value={item.received_qty}
                                onChange={(e) => updateItemField(index, 'received_qty', e.target.value)}
                                className="w-24 text-right"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="0"
                                max={item.received_qty}
                                value={item.accepted_qty}
                                onChange={(e) => updateItemField(index, 'accepted_qty', e.target.value)}
                                className="w-24 text-right"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unit_price}
                                onChange={(e) => updateItemField(index, 'unit_price', e.target.value)}
                                className="w-24 text-right"
                              />
                            </TableCell>
                            <TableCell>
                              <Select
                                value={String(item.tax_rate)}
                                onValueChange={(v) => updateItemField(index, 'tax_rate', parseFloat(v))}
                              >
                                <SelectTrigger className="w-20">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                  {TAX_RATES.map(rate => (
                                    <SelectItem key={rate.value} value={String(rate.value)}>
                                      {rate.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Input
                                value={item.batch_number}
                                onChange={(e) => updateItemField(index, 'batch_number', e.target.value)}
                                placeholder="Batch"
                                className="w-28"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="date"
                                value={item.expiry_date}
                                onChange={(e) => updateItemField(index, 'expiry_date', e.target.value)}
                                className="w-32"
                              />
                            </TableCell>
                            <TableCell className="text-right font-medium text-gray-800">
                              ₹{calculateLineTotal(item).toFixed(2)}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeItem(index)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Totals */}
                {grnItems.length > 0 && (
                  <div className="flex justify-end">
                    <div className="w-full max-w-sm space-y-2 p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-medium">₹{totals.subtotal}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total Tax:</span>
                        <span className="font-medium">₹{totals.totalTax}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold border-t pt-2">
                        <span>Grand Total:</span>
                        <span className="text-teal-600">₹{totals.grandTotal}</span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <Label className="text-gray-700">Notes / Remarks (Optional)</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any notes about this GRN..."
                    rows={2}
                    className="bg-white"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <Button
                variant="outline"
                onClick={resetForm}
                disabled={submitting}
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" /> Clear Form
              </Button>
              <Button
                variant="outline"
                onClick={() => handleSaveGRN(false)}
                disabled={submitting || grnItems.length === 0}
                className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                <Save className="w-4 h-4" />
                {submitting ? 'Saving...' : 'Save as Draft'}
              </Button>
              <Button
                onClick={() => handleSaveGRN(true)}
                disabled={submitting || grnItems.length === 0}
                className="gap-2 bg-teal-600 hover:bg-teal-700"
              >
                <CheckCircle className="w-4 h-4" />
                {submitting ? 'Processing...' : 'Complete & Update Stock'}
              </Button>
            </div>
          </TabsContent>

          {/* GRN History Tab */}
          <TabsContent value="history" className="mt-0 space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label className="text-gray-600">Status:</Label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-[160px]">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* GRN List */}
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>GRN #</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Invoice #</TableHead>
                      <TableHead className="text-center">Items</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {listLoading ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-10 text-gray-500">
                          Loading GRNs...
                        </TableCell>
                      </TableRow>
                    ) : grnList.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-10 text-gray-500">
                          <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p className="font-medium">No GRNs found</p>
                          <p className="text-sm">Create your first GRN to get started</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      grnList.map(grn => (
                        <TableRow key={grn.id} className="hover:bg-gray-50">
                          <TableCell>
                            <span className="font-mono text-sm text-teal-600 font-medium">
                              {grn.grn_number}
                            </span>
                          </TableCell>
                          <TableCell>
                            <p className="font-medium text-gray-800">{grn.vendor_name}</p>
                            <p className="text-xs text-gray-500">{grn.vendor_code}</p>
                          </TableCell>
                          <TableCell className="text-gray-600">
                            {grn.invoice_number || '-'}
                          </TableCell>
                          <TableCell className="text-center font-medium">
                            {grn.items?.length || 0}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            ₹{(grn.grand_total || 0).toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              grn.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                              grn.status === 'draft' ? 'bg-amber-100 text-amber-700' :
                              'bg-gray-100 text-gray-600'
                            }>
                              {grn.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-gray-500 text-sm">
                            {formatDate(grn.created_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewGRN(grn)}
                              className="text-gray-600 hover:text-teal-600"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default StoreGRNEntry;
